import React, { useMemo, useState, useEffect } from "react";
import { Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import SchoolCard from "../components/SchoolCard";
import FilterChip from "../components/FilterChip";
import Button from "../components/Button";
import { schools, CITIES, LEVELS, CURRICULA } from "../data/schools";
import SEO from "../components/SEO";

const BUDGET_OPTIONS = [
  { label: "Semua", value: null },
  { label: "≤ Rp1,5 jt", value: 1500000 },
  { label: "≤ Rp2,5 jt", value: 2500000 },
  { label: "≤ Rp3,5 jt", value: 3500000 },
  { label: "≤ Rp5 jt", value: 5000000 },
];

export default function Schools() {
  const [query, setQuery] = useState("");
  const [city, setCity] = useState(null);
  const [level, setLevel] = useState(null);
  const [budget, setBudget] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showMobileFilters) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showMobileFilters]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return schools.filter((s) => {
      if (q) {
        const hay = `${s.name} ${s.city} ${s.district} ${s.curriculum} ${s.tags.join(" ")} ${s.extracurriculars.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (city && s.city !== city) return false;
      if (level && !s.educationLevels.includes(level)) return false;
      if (budget && s.monthlyTuition > budget) return false;
      if (curriculum && s.curriculum !== curriculum) return false;
      return true;
    });
  }, [query, city, level, budget, curriculum]);

  const hasFilters = city || level || budget || curriculum;

  const reset = () => {
    setCity(null);
    setLevel(null);
    setBudget(null);
    setCurriculum(null);
  };

  const FiltersContent = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-extrabold text-lg">Filter</h2>
        {hasFilters && (
          <button
            onClick={reset}
            className="text-sm font-semibold text-purple hover:underline"
            data-testid="reset-filters"
          >
            Reset Filter
          </button>
        )}
      </div>

      <FilterSection label="Kota">
        <FilterChip active={!city} onClick={() => setCity(null)} testId="city-all">
          Semua
        </FilterChip>
        {CITIES.map((c) => (
          <FilterChip key={c} active={city === c} onClick={() => setCity(c)} testId={`city-${c}`}>
            {c}
          </FilterChip>
        ))}
      </FilterSection>

      <FilterSection label="Jenjang">
        <FilterChip active={!level} onClick={() => setLevel(null)} testId="level-all">
          Semua
        </FilterChip>
        {LEVELS.map((l) => (
          <FilterChip key={l} active={level === l} onClick={() => setLevel(l)} testId={`level-${l}`}>
            {l}
          </FilterChip>
        ))}
      </FilterSection>

      <FilterSection label="Budget">
        {BUDGET_OPTIONS.map((b) => (
          <FilterChip
            key={b.label}
            active={budget === b.value}
            onClick={() => setBudget(b.value)}
            testId={`budget-${b.label}`}
          >
            {b.label}
          </FilterChip>
        ))}
      </FilterSection>

      <FilterSection label="Kurikulum">
        <FilterChip active={!curriculum} onClick={() => setCurriculum(null)} testId="curriculum-all">
          Semua
        </FilterChip>
        {CURRICULA.map((c) => (
          <FilterChip
            key={c}
            active={curriculum === c}
            onClick={() => setCurriculum(c)}
            testId={`curriculum-${c}`}
          >
            {c}
          </FilterChip>
        ))}
      </FilterSection>
    </div>
  );

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 relative">
      <SEO
        title="Jelajahi Sekolah | MySchoolMate"
        description="Jelajahi pilihan sekolah dan lihat informasi biaya, lokasi, kurikulum, fasilitas, dan program dalam satu tempat."
        canonicalPath="/schools"
      />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div className="max-w-xl">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Temukan sekolah yang sesuai untuk keluarga Anda.
          </h1>
          <p className="mt-3 text-ink/70 text-lg">
            Jelajahi pilihan sekolah unggulan atau gunakan AI Match.
          </p>
        </div>
        <Button to="/match" variant="yellow" className="shrink-0 text-base h-12" data-testid="explore-ai-match">
          <Sparkles size={20} /> AI School Match
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start relative">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 xl:w-72 shrink-0 bg-warm ink-border rounded-card p-6">
          <FiltersContent />
        </aside>

        {/* Main Results */}
        <div className="flex-1 w-full min-w-0">
          {/* Search Bar */}
          <div className="relative mb-6 flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/50" size={20} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari sekolah, kota, kurikulum, atau program..."
                data-testid="schools-search"
                className="w-full bg-warm ink-border rounded-pill pl-12 pr-4 py-3.5 font-medium placeholder:text-ink/50 focus:outline-none focus:ring-4 focus:ring-purple/20 transition-shadow"
              />
            </div>
            <button
              className="lg:hidden flex shrink-0 items-center justify-center gap-2 bg-warm ink-border px-5 rounded-pill font-bold hover:bg-cream-2 transition-colors"
              onClick={() => setShowMobileFilters(true)}
              data-testid="mobile-filter-btn"
            >
              <SlidersHorizontal size={18} /> Filter
            </button>
          </div>

          {/* Result count */}
          <p className="font-bold text-lg mb-4 text-ink" data-testid="results-count">
            {results.length} sekolah ditemukan
          </p>

          {/* Grid */}
          {results.length > 0 ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="schools-grid">
              {results.map((s) => (
                <SchoolCard key={s.id} school={s} />
              ))}
            </div>
          ) : (
            <div className="mt-8 bg-warm ink-border rounded-card p-12 text-center">
              <Search size={48} className="mx-auto mb-4 text-ink/20" />
              <p className="text-2xl font-extrabold mb-2">Belum ada sekolah yang cocok</p>
              <p className="text-ink/60 mb-6">Coba ubah kata kunci atau kurangi filter Anda.</p>
              <button onClick={() => { setQuery(""); reset(); }} className="font-bold text-purple hover:underline">
                Reset semua pencarian & filter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[100] flex lg:hidden">
          <div
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm transition-opacity"
            onClick={() => setShowMobileFilters(false)}
            aria-hidden="true"
          />
          <div className="relative w-4/5 max-w-sm bg-cream h-full border-r-2 border-ink shadow-offset flex flex-col animate-fade-right">
            <div className="p-4 border-b-2 border-ink flex items-center justify-between bg-warm">
              <span className="font-extrabold text-lg">Filter Pencarian</span>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-1.5 rounded-lg hover:bg-cream-2 transition-colors"
                aria-label="Tutup filter"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              <FiltersContent />
            </div>
            <div className="p-4 border-t-2 border-ink bg-warm">
              <Button size="lg" className="w-full" onClick={() => setShowMobileFilters(false)}>
                Tampilkan {results.length} Hasil
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSection({ label, children }) {
  return (
    <div className="flex flex-col gap-2.5">
      <h3 className="text-sm font-extrabold text-ink/70 uppercase tracking-wider">{label}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
